import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { UserService } from '../services/user.service';
import { AppStateResetService } from '../services/app-state-reset.service';

const RECENT_LIMIT = 5;

@Injectable({ providedIn: 'root' })
export class SearchHistoryStore {
  private readonly user = inject(UserService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly appReset = inject(AppStateResetService);

  readonly recent = signal<string[]>([]);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // The read itself is inside the try (CodeAnt review, PR #263): `getItem` can throw,
      // not just `JSON.parse` — e.g. Safari private-mode / storage-disabled browsers reject
      // ANY sessionStorage access. This store is root-provided and read by the always-on
      // header, so an uncaught throw here would take down the whole app shell instead of
      // degrading to an empty in-memory history.
      try {
        const stored = sessionStorage.getItem(this.key());
        if (stored) {
          const parsed: unknown = JSON.parse(stored);
          // A validly-parsed but wrong-shaped value (an object, an array of non-strings, …)
          // is just as unsafe to trust as malformed JSON — the always-on header's `@for` loop
          // over `recent()` would throw on a non-string entry, taking down the whole shell.
          if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
            this.recent.set(parsed);
          }
        }
      } catch { /* storage unavailable or corrupt value, start empty */ }
    }
    this.appReset.register(() => {
      this.recent.set([]);
      if (isPlatformBrowser(this.platformId)) sessionStorage.removeItem(this.key());
    });
  }

  private key(): string {
    return `search-history:${this.user.getCustomerId()}|${this.user.getUserId()}`;
  }

  record(query: string): void {
    const next = [query, ...this.recent().filter((q) => q !== query)].slice(0, RECENT_LIMIT);
    this.recent.set(next);
    if (isPlatformBrowser(this.platformId)) {
      // Persistence is best-effort: a quota-exceeded or privacy-mode `setItem` throw must not
      // propagate. `record()` runs as the FIRST statement inside `IntentSearchComponent`'s
      // blur handlers — an uncaught throw here would skip the rest of the handler and wedge
      // the suggestions panel open. Degrade to in-memory-only rather than break the feature.
      try { sessionStorage.setItem(this.key(), JSON.stringify(next)); } catch { /* storage unavailable, in-memory state above still applies */ }
    }
  }
}
